'use client';

import { useAction, useConvexAuth, useMutation, useQuery } from 'convex/react';
import { useCallback, useEffect, useRef } from 'react';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import {
  trackPendingVoiceRecordingUpload,
  waitForPendingVoiceRecordingUploads,
  type PendingVoiceRecordingUploadsBySession,
} from '@/lib/realtime-voice-recording-uploads';
import {
  VOICE_RECORD_ASSISTANT_AUDIO,
  VOICE_RECORD_USER_AUDIO,
  VOICE_RECORDING_ENABLED,
  VOICE_RECORDING_MAX_CLIP_MS,
  getRealtimeVoiceRecordingFilename,
  isRealtimeVoiceRecordingRoleEnabled,
  selectRealtimeVoiceRecordingMimeType,
  shouldKeepRealtimeVoiceRecordingClip,
  type VoiceRecordingRole,
} from '@/lib/realtime-voice-recording-config';
import {
  stopRealtimeVoiceMediaImmediately,
  uploadRealtimeVoiceRecordingBlob,
} from '@/components/org-chat/realtime-voice-lifecycle';

export type VoiceRecordingSessionState = {
  voiceSessionId: Id<'voiceSessions'>;
  orgSlug: string;
};

type ActiveVoiceTurnRecording = {
  role: VoiceRecordingRole;
  recordingSession: VoiceRecordingSessionState;
  sequence: number;
  startedAt: number;
  mimeType?: string;
  recorder: MediaRecorder;
  chunks: Blob[];
  maxClipTimer: ReturnType<typeof setTimeout> | null;
  done: Promise<void>;
  resolveDone: () => void;
};

export function useVoiceRecordingController(args: {
  open: boolean;
  orgThreadId?: Id<'orgThreads'>;
  orgSlug: string;
  organizationId?: Id<'organizations'>;
}) {
  const generateUploadUrl = useMutation(
    api.media.uploads.generatePrivateUploadUrlForMedia
  );
  const startSession = useMutation(api.voice.recordings.startSession);
  const endSession = useMutation(api.voice.recordings.endSession);
  const reserveTurn = useMutation(api.voice.recordings.reserveTurn);
  const finalizeTurn = useAction(
    api.voice.recording_finalize_actions.finalizeTurnUpload
  );
  const failTurn = useMutation(api.voice.recordings.failTurn);
  // Sign-out can land mid-upload; the chain stops instead of calling
  // protected functions without an identity.
  const { isAuthenticated } = useConvexAuth();
  const signedInRef = useRef(isAuthenticated);
  useEffect(() => {
    signedInRef.current = isAuthenticated;
  }, [isAuthenticated]);
  const settings = useQuery(
    api.voice.recordings.getRecordingSettings,
    args.open && args.orgThreadId ? { orgThreadId: args.orgThreadId } : 'skip'
  );
  const settingsLoading =
    args.open && Boolean(args.orgThreadId) && settings === undefined;
  const recordUserAudio = settingsLoading
    ? false
    : (settings?.recordUserAudio ?? VOICE_RECORD_USER_AUDIO);
  const recordAssistantAudio = settingsLoading
    ? false
    : (settings?.recordAssistantAudio ?? VOICE_RECORD_ASSISTANT_AUDIO);
  const enabled =
    VOICE_RECORDING_ENABLED &&
    !settingsLoading &&
    (settings?.recordingEnabled ?? true) &&
    (recordUserAudio || recordAssistantAudio);

  const sessionRef = useRef<VoiceRecordingSessionState | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const assistantPlaybackActiveRef = useRef(false);
  const assistantRecordingActiveRef = useRef(false);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const sequenceRef = useRef(0);
  const turnsRef = useRef<{
    user: ActiveVoiceTurnRecording | null;
    assistant: ActiveVoiceTurnRecording | null;
  }>({ user: null, assistant: null });
  const pendingUploadsRef = useRef<PendingVoiceRecordingUploadsBySession>(
    new Map()
  );

  const trackUpload = useCallback(
    (voiceSessionId: Id<'voiceSessions'>, upload: Promise<void>) => {
      trackPendingVoiceRecordingUpload({
        uploadsBySession: pendingUploadsRef.current,
        voiceSessionId: String(voiceSessionId),
        upload,
        onTrackerError: (error) => {
          console.warn('[voice-call] voice recording upload tracker failed', {
            error,
          });
        },
      });
    },
    []
  );

  const uploadClip = useCallback(
    async (input: {
      active: ActiveVoiceTurnRecording;
      blob: Blob;
      stoppedAt: number;
      durationMs: number;
    }) => {
      const contentType =
        input.blob.type || input.active.mimeType || 'audio/webm';
      let voiceTurnId: Id<'voiceTurns'> | null = null;
      try {
        const reserved = await reserveTurn({
          voiceSessionId: input.active.recordingSession.voiceSessionId,
          role: input.active.role,
          sequence: input.active.sequence,
          startedAt: input.active.startedAt,
        });
        if (!reserved.voiceTurnId || !signedInRef.current) return;
        voiceTurnId = reserved.voiceTurnId;
        const target = await generateUploadUrl({
          orgSlug: input.active.recordingSession.orgSlug || args.orgSlug,
          organizationId: args.organizationId,
          mediaType: 'audio',
          filename: getRealtimeVoiceRecordingFilename({
            role: input.active.role,
            sequence: input.active.sequence,
            mimeType: contentType,
          }),
          contentType,
          flow: 'voice_recording',
        });
        if (!signedInRef.current) return;
        const response = await uploadRealtimeVoiceRecordingBlob({
          uploadUrl: target.uploadUrl,
          contentType,
          blob: input.blob,
        });
        if (!response.ok) {
          throw new Error(`R2 upload failed with ${response.status}`);
        }
        if (!signedInRef.current) return;
        await finalizeTurn({
          voiceTurnId,
          stoppedAt: input.stoppedAt,
          durationMs: input.durationMs,
          storageKey: target.storageKey,
        });
      } catch (error) {
        if (!signedInRef.current) return;
        if (!voiceTurnId) {
          console.warn('[voice-call] voice recording could not be reserved', {
            error,
          });
          return;
        }
        try {
          await failTurn({
            voiceTurnId,
            stoppedAt: input.stoppedAt,
            durationMs: input.durationMs,
            error:
              error instanceof Error
                ? error.message
                : 'Voice recording upload failed',
          });
        } catch (failError) {
          console.warn('[voice-call] voice recording cleanup failed', {
            voiceTurnId,
            originalError: error,
            error: failError,
          });
        }
      }
    },
    [
      args.organizationId,
      args.orgSlug,
      failTurn,
      finalizeTurn,
      generateUploadUrl,
      reserveTurn,
    ]
  );

  const stopTurn = useCallback(
    (role: VoiceRecordingRole): Promise<void> => {
      const active = turnsRef.current[role];
      if (!active) return Promise.resolve();
      trackUpload(active.recordingSession.voiceSessionId, active.done);
      turnsRef.current[role] = null;
      if (active.maxClipTimer) clearTimeout(active.maxClipTimer);
      active.maxClipTimer = null;
      if (active.recorder.state !== 'inactive') active.recorder.stop();
      return active.done;
    },
    [trackUpload]
  );

  const handleStop = useCallback(
    (active: ActiveVoiceTurnRecording) => {
      if (turnsRef.current[active.role] === active)
        turnsRef.current[active.role] = null;
      if (active.maxClipTimer) clearTimeout(active.maxClipTimer);
      active.maxClipTimer = null;
      const stoppedAt = Date.now();
      const rawDurationMs = stoppedAt - active.startedAt;
      const contentType =
        active.mimeType || active.chunks.find((chunk) => chunk.type)?.type;
      const blob = new Blob(active.chunks, {
        type: contentType || 'audio/webm',
      });
      if (
        !shouldKeepRealtimeVoiceRecordingClip({
          durationMs: rawDurationMs,
          byteSize: blob.size,
        })
      ) {
        active.resolveDone();
        return;
      }
      const upload = uploadClip({
        active,
        blob,
        stoppedAt,
        durationMs: rawDurationMs,
      })
        .catch((error) => {
          console.warn('[voice-call] voice recording upload failed', { error });
        })
        .finally(active.resolveDone);
      trackUpload(active.recordingSession.voiceSessionId, upload);
    },
    [trackUpload, uploadClip]
  );

  const startTurn = useCallback(
    function startTurnRecording(
      role: VoiceRecordingRole,
      stream: MediaStream | null
    ) {
      const recordingSession = sessionRef.current;
      if (!enabled || !recordingSession) return;
      if (
        !isRealtimeVoiceRecordingRoleEnabled({
          role,
          recordUserAudio,
          recordAssistantAudio,
        }) ||
        !stream?.getAudioTracks().length ||
        typeof MediaRecorder === 'undefined' ||
        turnsRef.current[role]
      ) {
        return;
      }
      const mimeType = selectRealtimeVoiceRecordingMimeType();
      let recorder: MediaRecorder;
      try {
        recorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);
      } catch (error) {
        console.warn('[voice-call] voice recording unavailable', {
          role,
          error,
        });
        return;
      }
      let resolveDone = () => {};
      const done = new Promise<void>((resolve) => {
        resolveDone = resolve;
      });
      const active: ActiveVoiceTurnRecording = {
        role,
        recordingSession,
        sequence: ++sequenceRef.current,
        startedAt: Date.now(),
        mimeType: recorder.mimeType || mimeType,
        recorder,
        chunks: [],
        maxClipTimer: null,
        done,
        resolveDone,
      };
      recorder.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) active.chunks.push(event.data);
      });
      recorder.addEventListener('stop', () => handleStop(active));
      recorder.addEventListener('error', (event) => {
        console.warn('[voice-call] voice recording failed', { role, event });
      });
      turnsRef.current[role] = active;
      try {
        recorder.start();
      } catch (error) {
        turnsRef.current[role] = null;
        active.resolveDone();
        console.warn('[voice-call] voice recording failed to start', {
          role,
          error,
        });
        return;
      }
      active.maxClipTimer = setTimeout(() => {
        if (
          turnsRef.current[role] !== active ||
          sessionRef.current !== recordingSession
        )
          return;
        // Live audio can continue without a provider turn boundary. Rotate
        // the clip immediately; its upload keeps the original session identity.
        void stopTurn(role);
        startTurnRecording(role, stream);
      }, VOICE_RECORDING_MAX_CLIP_MS);
    },
    [enabled, handleStop, recordAssistantAudio, recordUserAudio, stopTurn]
  );

  const stopAllTurns = useCallback(async () => {
    await Promise.allSettled([stopTurn('user'), stopTurn('assistant')]);
  }, [stopTurn]);

  const finishSession = useCallback(
    (endedAt = Date.now()) => {
      const recordingSession = sessionRef.current;
      const micStream = micStreamRef.current;
      const audioElement = audioElementRef.current;
      sessionRef.current = null;
      micStreamRef.current = null;
      remoteStreamRef.current = null;
      assistantPlaybackActiveRef.current = false;
      assistantRecordingActiveRef.current = false;
      audioElementRef.current = null;
      const turnsStopped = stopRealtimeVoiceMediaImmediately({
        stopTurns: stopAllTurns,
        stopMedia: () => {
          micStream?.getTracks().forEach((track) => track.stop());
          if (audioElement) audioElement.srcObject = null;
        },
      });
      void (async () => {
        await turnsStopped;
        if (!recordingSession) return;
        await waitForPendingVoiceRecordingUploads({
          uploadsBySession: pendingUploadsRef.current,
          voiceSessionId: String(recordingSession.voiceSessionId),
        });
        await endSession({
          voiceSessionId: recordingSession.voiceSessionId,
          endedAt,
        }).catch((error) => {
          console.warn('[voice-call] failed to end voice recording session', {
            error,
          });
        });
      })();
    },
    [endSession, stopAllTurns]
  );

  return {
    settingsLoading,
    recordUserAudio,
    recordAssistantAudio,
    enabled,
    startSession,
    endSession,
    sessionRef,
    micStreamRef,
    remoteStreamRef,
    assistantPlaybackActiveRef,
    assistantRecordingActiveRef,
    audioElementRef,
    sequenceRef,
    startTurn,
    stopTurn,
    finishSession,
  };
}
